import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { VoiceSelector } from "../components/VoiceSelector";
import { ParamSlider } from "../components/ParamSlider";
import { AudioTrimmer } from "../components/AudioTrimmer";
import { WaveformTrimmer } from "../components/WaveformTrimmer";
import { EmotionTagAutocomplete } from "../components/EmotionTagAutocomplete";
import { RichScript, type RichScriptHandle } from "../components/RichScript";
import { Icon } from "../components/icons";
import { submitJob, updateReferenceDefaults } from "../lib/api";
import { useDataCache } from "../contexts/DataCacheContext";
import { effectiveReferenceKey } from "../lib/types";
import type { MossCloneMode, Reference, TtsModel } from "../lib/types";

const LONG_AUDIO_THRESHOLD_S = 120;

type Step = 0 | 1; // 0 = Voice, 1 = Script

const DEFAULT_PARAMS = {
  exaggeration: 0.5,
  cfgWeight: 0.5,
  temperature: 0.8,
  repetitionPenalty: 1.1,
  topP: 0.95,
  minP: 0.05,
};

interface RetryState {
  text?: string;
  referenceName?: string;
  referenceKey?: string;
  model?: TtsModel;
  exaggeration?: number;
  cfgWeight?: number;
  temperature?: number;
  repetitionPenalty?: number;
  topP?: number;
  minP?: number;
  trimStart?: number;
  trimEnd?: number;
  language?: string;
  cloneMode?: MossCloneMode;
}

const MODEL_META: Record<
  TtsModel,
  { label: string; tag: "studio" | "balanced" | "fast"; quality: number; desc: string; feats: string[] }
> = {
  "fish-s2-pro": {
    label: "Fish S2-Pro",
    tag: "studio",
    quality: 96,
    desc: "Studio fidelity. Captures breath, lip noise, micro-pauses.",
    feats: ["Emotion tags", "Voice cloning", "48 kHz"],
  },
  "qwen3-tts": {
    label: "Qwen3-TTS",
    tag: "balanced",
    quality: 78,
    desc: "Multilingual. Handles 16 languages with native prosody.",
    feats: ["16 languages", "Code-switching", "Long-form"],
  },
  "moss-tts-local": {
    label: "MOSS-TTS 1.5 Local",
    tag: "studio",
    quality: 94,
    desc: "Closest speaker match in MOSS's own eval (SIM 73.3 EN / 79.6 ZH).",
    feats: ["Best likeness", "48 kHz stereo", "31 languages"],
  },
  "moss-tts-delay": {
    label: "MOSS-TTS 1.5 8B",
    tag: "studio",
    quality: 92,
    desc: "The 8B flagship. Scores lower on speaker similarity than Local.",
    feats: ["8B flagship", "24 kHz mono", "31 languages"],
  },
  chatterbox: {
    label: "Chatterbox",
    tag: "fast",
    quality: 62,
    desc: "Lightweight. Best for sub-30s clips at low cost.",
    feats: ["Tunable", "Low latency", "SSML"],
  },
};

const MODEL_ORDER: TtsModel[] = [
  "fish-s2-pro",
  "moss-tts-local",
  "moss-tts-delay",
  "qwen3-tts",
  "chatterbox",
];

/** Both MOSS-TTS 1.5 checkpoints share their whole request shape. */
function isMoss(model: TtsModel): boolean {
  return model === "moss-tts-local" || model === "moss-tts-delay";
}

/** MOSS's own quickstart default for audio_temperature. */
const MOSS_DEFAULT_TEMPERATURE = 1.7;

const LANGUAGE_OPTIONS: Array<[string, string]> = [
  ["auto", "Auto-detect"],
  ["en", "English"],
  ["zh", "Chinese"],
  ["ja", "Japanese"],
  ["ko", "Korean"],
  ["es", "Spanish"],
  ["pt", "Portuguese"],
  ["ar", "Arabic"],
  ["ru", "Russian"],
  ["fr", "French"],
  ["de", "German"],
  ["it", "Italian"],
  ["nl", "Dutch"],
  ["pl", "Polish"],
  ["tr", "Turkish"],
  ["sv", "Swedish"],
  ["hi", "Hindi"],
  ["th", "Thai"],
  ["vi", "Vietnamese"],
];

const EMOTIONS = [
  "[happy]",
  "[sad]",
  "[angry]",
  "[excited]",
  "[calm]",
  "[nervous]",
  "[confident]",
  "[whisper]",
  "[laughing]",
];

function voiceInitials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((s) => s[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

export function Generate() {
  const navigate = useNavigate();
  const location = useLocation();
  const { optimisticInsert } = useDataCache();
  const retryState = location.state as RetryState | null;

  const [step, setStep] = useState<Step>(retryState?.referenceName ? 1 : 0);
  const [selectedVoice, setSelectedVoice] = useState<Reference | null>(
    retryState?.referenceName
      ? {
          name: retryState.referenceName,
          s3Key: retryState.referenceKey ?? "",
          duration: 0,
          uploadedAt: "",
        }
      : null
  );
  const [text, setText] = useState(retryState?.text ?? "");
  const [model, setModel] = useState<TtsModel>(retryState?.model ?? "fish-s2-pro");
  const [language, setLanguage] = useState<string>(retryState?.language ?? "auto");
  // MOSS-TTS likeness controls. Both defaults are measured, not assumed:
  // audio-only prompting beat transcript-conditioning on identity AND pacing in
  // all six checkpoint/temperature pairings, and MOSS's own 1.7 temperature was
  // the only setting that held sane durations. See worker/eval_moss_likeness.py.
  const [cloneMode, setCloneMode] = useState<MossCloneMode>(
    retryState?.cloneMode ?? "reference"
  );
  const [mossTemp, setMossTemp] = useState<number>(
    retryState?.temperature ?? MOSS_DEFAULT_TEMPERATURE
  );
  const [params, setParams] = useState(() => {
    if (retryState) {
      return {
        exaggeration: retryState.exaggeration ?? DEFAULT_PARAMS.exaggeration,
        cfgWeight: retryState.cfgWeight ?? DEFAULT_PARAMS.cfgWeight,
        temperature: retryState.temperature ?? DEFAULT_PARAMS.temperature,
        repetitionPenalty: retryState.repetitionPenalty ?? DEFAULT_PARAMS.repetitionPenalty,
        topP: retryState.topP ?? DEFAULT_PARAMS.topP,
        minP: retryState.minP ?? DEFAULT_PARAMS.minP,
      };
    }
    return DEFAULT_PARAMS;
  });
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [trimOpen, setTrimOpen] = useState(false);
  const [defaultsAppliedFor, setDefaultsAppliedFor] = useState<string | null>(
    retryState?.referenceName ?? null
  );
  const [trimStart, setTrimStart] = useState(retryState?.trimStart ?? 0);
  const [trimEnd, setTrimEnd] = useState(retryState?.trimEnd ?? 60);
  const [editingDefault, setEditingDefault] = useState(false);
  const [emoFlash, setEmoFlash] = useState<string | null>(null);
  const richScriptRef = useRef<RichScriptHandle>(null);
  const [modelExpanded, setModelExpanded] = useState(false);

  useEffect(() => {
    if (retryState) {
      window.history.replaceState({}, "");
    }
  }, []);

  const selMeta = MODEL_META[model];

  const handleSelectVoice = (ref: Reference) => {
    setSelectedVoice(ref);
    setEditingDefault(false);
    if (ref.defaults && defaultsAppliedFor !== ref.name) {
      setParams((prev) => ({
        exaggeration: ref.defaults?.exaggeration ?? prev.exaggeration,
        cfgWeight: ref.defaults?.cfgWeight ?? prev.cfgWeight,
        temperature: ref.defaults?.temperature ?? prev.temperature,
        repetitionPenalty: ref.defaults?.repetitionPenalty ?? prev.repetitionPenalty,
        topP: ref.defaults?.topP ?? prev.topP,
        minP: ref.defaults?.minP ?? prev.minP,
      }));
    }
    if (defaultsAppliedFor !== ref.name && !retryState?.trimStart && !retryState?.trimEnd) {
      const defaultStart = ref.defaultTrimStart ?? 0;
      const defaultEnd = ref.defaultTrimEnd ?? Math.min(ref.duration, 60);
      setTrimStart(defaultStart);
      setTrimEnd(defaultEnd);
    }
    setDefaultsAppliedFor(ref.name);
    setStep(1);
  };

  const addEmotion = (e: string) => {
    setText((prev) => (prev + " " + e).trim() + " ");
    setEmoFlash(e);
    setTimeout(() => setEmoFlash(null), 700);
    // Keep typing flow uninterrupted — return focus to the script editor
    // after the React state has flushed the new content into the DOM.
    requestAnimationFrame(() => richScriptRef.current?.focusEnd());
  };

  const handleSubmit = async () => {
    if (!selectedVoice || !text.trim()) return;
    setSubmitting(true);
    setError(null);

    const hasClip = selectedVoice.defaultClipS3Key != null;
    const submitKey = effectiveReferenceKey(selectedVoice);
    const usesTrim =
      model === "qwen3-tts" || model === "fish-s2-pro" || isMoss(model);
    // MOSS's own default is 1.7; only send a temperature when it's been moved
    // off that, so the worker keeps applying upstream defaults otherwise.
    const mossParams = {
      cloneMode,
      ...(mossTemp !== MOSS_DEFAULT_TEMPERATURE ? { temperature: mossTemp } : {}),
    };

    try {
      const jobData: Parameters<typeof submitJob>[0] = {
        type: "tts",
        text: text.trim(),
        referenceName: selectedVoice.name,
        referenceKey: submitKey,
        model,
        ...(usesTrim && !hasClip ? { trimStart, trimEnd } : {}),
        ...(model === "fish-s2-pro" || isMoss(model) ? { language } : {}),
        ...(isMoss(model) ? mossParams : {}),
        ...(model === "chatterbox" ? params : {}),
      };
      const result = await submitJob(jobData);

      if (
        usesTrim &&
        selectedVoice.defaultTrimStart == null &&
        selectedVoice.defaultTrimEnd == null
      ) {
        updateReferenceDefaults(selectedVoice.name, trimStart, trimEnd).catch(() => {});
      }

      optimisticInsert({
        jobId: result.jobId,
        type: "tts",
        status: "queued",
        text: text.trim(),
        referenceName: selectedVoice.name,
        referenceKey: submitKey,
        model,
        ...(model === "chatterbox" ? params : {}),
        ...(usesTrim && !hasClip ? { trimStart, trimEnd } : {}),
        ...(model === "fish-s2-pro" || isMoss(model) ? { language } : {}),
        ...(isMoss(model) ? mossParams : {}),
      });
      navigate("/jobs");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to submit sample");
      setSubmitting(false);
    }
  };

  const stepLabels = ["Voice", "Script"] as const;
  const progressPct = useMemo(() => {
    if (step === 0) return 0;
    const textWeight = Math.min(1, text.length / 40);
    return ((step + textWeight) / 2) * 100;
  }, [step, text.length]);

  return (
    <div className="content">
      <div className="ph">
        <div>
          <div className="ph-eyebrow">Generate</div>
          <h1 className="ph-title">Create speech in two steps</h1>
          <div className="ph-lede">
            Pick a voice, write your script, get takes. We'll route you to Samples to pick a favorite.
          </div>
        </div>
        <div className="ph-actions">
          <button
            type="button"
            className={`btn btn-ghost${step === 0 ? " disabled" : ""}`}
            onClick={() => {
              setStep(0);
              setText("");
              setSelectedVoice(null);
              setError(null);
            }}
          >
            <Icon.Refresh /> Reset
          </button>
        </div>
      </div>

      {error && <div className="err-banner">{error}</div>}

      <div className="wiz">
        <div className="stepper-line">
          {stepLabels.map((label, i) => (
            <button
              key={label}
              type="button"
              className={`seg${i < step ? " done" : ""}${i === step ? " cur" : ""}`}
              onClick={() => i <= step && setStep(i as Step)}
            >
              <span className="seg-num">{i + 1}</span>
              <span className="seg-lbl">{label}</span>
              <span
                className="seg-fill"
                style={{
                  width:
                    i < step
                      ? "100%"
                      : i === step
                        ? Math.min(100, Math.max(0, progressPct * 2 - i * 100)) + "%"
                        : "0%",
                }}
              />
            </button>
          ))}
        </div>

        <div className="wiz-stage">
          <div className="wiz-stage-inner" key={step}>
            {step === 0 && (
              <div>
                <div className="step-h">
                  <div className="step-h-q">Pick a voice</div>
                  <div className="step-h-d">
                    Curated references and your own uploads. Click to select; the choice persists across
                    steps.
                  </div>
                </div>
                <VoiceSelector selected={selectedVoice} onSelect={handleSelectVoice} />
              </div>
            )}

            {step === 1 && selectedVoice && (
              <div>
                <div className="step-h">
                  <div className="step-h-q">Write your script</div>
                  <div className="step-h-d">
                    Choose a model based on fidelity vs. speed. Drop emotion tags inline for
                    expressive control on Fish.
                  </div>
                </div>

                <div className="ctx-bar">
                  <span className="lbl">Voice</span>
                  <span className="v">
                    <span className="vc-avatar">{voiceInitials(selectedVoice.name)}</span>
                    {selectedVoice.name}
                  </span>
                  <span className="sp" />
                  <button type="button" className="change" onClick={() => setStep(0)}>
                    change →
                  </button>
                </div>

                {/* Mobile collapsed model selector */}
                <button
                  type="button"
                  className="model-collapsed"
                  onClick={() => setModelExpanded((v) => !v)}
                  aria-expanded={modelExpanded}
                >
                  <span className="mc-lbl">Model</span>
                  <span className="mc-name">{selMeta.label}</span>
                  <span className={`mc-tag t-${selMeta.tag}`}>{selMeta.tag}</span>
                  <span className="mc-q">{selMeta.quality}</span>
                  <span className="sp" />
                  <span className="mc-arrow" data-open={modelExpanded}>
                    <Icon.Caret />
                  </span>
                </button>

                <div className={`model-row${modelExpanded ? " expanded" : ""}`}>
                  {MODEL_ORDER.map((m) => {
                    const meta = MODEL_META[m];
                    const sel = model === m;
                    return (
                      <div
                        key={m}
                        className={`mc2${sel ? " on" : ""}`}
                        onClick={() => {
                          setModel(m);
                          setModelExpanded(false);
                        }}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            setModel(m);
                          }
                        }}
                      >
                        <div className="mc2-radio">
                          <span className="mc2-radio-dot" />
                        </div>
                        <div className="mc2-body">
                          <div className="mc2-head">
                            <div className="mc2-name">{meta.label}</div>
                            <span className={`mc2-tag t-${meta.tag}`}>{meta.tag}</span>
                          </div>
                          <div className="mc2-desc">{meta.desc}</div>
                          <div className="mc2-meter">
                            <span className="mc2-meter-lbl">quality</span>
                            <span className="mc2-meter-bar">
                              <i style={{ width: meta.quality + "%" }} />
                            </span>
                            <span className="mc2-meter-val">{meta.quality}</span>
                          </div>
                          <div className="mc2-foot">
                            {meta.feats.map((f) => (
                              <span key={f} className="mc2-chip">
                                {f}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Language picker — Fish and both MOSS checkpoints */}
                {(model === "fish-s2-pro" || isMoss(model)) && (
                  <div className="ctx-bar" style={{ marginBottom: 14 }}>
                    <span className="lbl">Language</span>
                    <select
                      value={language}
                      onChange={(e) => setLanguage(e.target.value)}
                      className="field-select"
                      style={{ maxWidth: 220, padding: "6px 30px 6px 10px", fontSize: 13 }}
                    >
                      {LANGUAGE_OPTIONS.map(([code, label]) => (
                        <option key={code} value={code}>
                          {label}
                        </option>
                      ))}
                    </select>
                    <span className="sp" />
                  </div>
                )}

                {/* Script editor */}
                <div className="ta-wrap">
                  <div className="ta-bar">
                    <span>Script</span>
                    <span style={{ display: "flex", gap: 14 }}>
                      <span className="lang">
                        {model === "fish-s2-pro" ? `${language} · auto` : "text"}
                      </span>
                      <span>{text.length} / 2000</span>
                    </span>
                  </div>
                  {model === "fish-s2-pro" ? (
                    <RichScript
                      ref={richScriptRef}
                      text={text}
                      onChange={setText}
                      placeholder="Type the words you want spoken… use [excited], [whisper], etc."
                    />
                  ) : (
                    <EmotionTagAutocomplete
                      value={text}
                      onChange={setText}
                      enabled={false}
                      placeholder="Type or paste the text you want spoken…"
                      className="ta"
                    />
                  )}

                  {model === "fish-s2-pro" && (
                    <>
                      <div className="emo-row">
                        <span className="emo-label">tag</span>
                        {EMOTIONS.map((e) => (
                          <button
                            type="button"
                            key={e}
                            className={`emo-tag${emoFlash === e ? " flash" : ""}`}
                            onClick={() => addEmotion(e)}
                          >
                            {e}
                          </button>
                        ))}
                      </div>
                      <div className="ta-hint">
                        Drop tags inline at sentence boundaries — try{" "}
                        <code>[whisper] hey, come here. [excited] you won!</code> Tags work best
                        between words, never mid-syllable.
                      </div>
                    </>
                  )}
                </div>

                {/* Reference Trim (Qwen3-TTS + Fish + MOSS) */}
                {(model === "qwen3-tts" || model === "fish-s2-pro" || isMoss(model)) &&
                  selectedVoice.previewUrl && (
                    <ReferenceTrim
                      voice={selectedVoice}
                      trimStart={trimStart}
                      trimEnd={trimEnd}
                      setTrimStart={setTrimStart}
                      setTrimEnd={setTrimEnd}
                      editingDefault={editingDefault}
                      setEditingDefault={setEditingDefault}
                      open={trimOpen}
                      setOpen={setTrimOpen}
                      onSavedDefault={(next) => setSelectedVoice(next)}
                    />
                  )}

                {/* MOSS: likeness controls */}
                {isMoss(model) && (
                  <div className="card" style={{ padding: 14, marginTop: 14 }}>
                    <div
                      style={{
                        fontSize: 12.5,
                        color: "var(--ink-2)",
                        fontWeight: 500,
                        marginBottom: 4,
                      }}
                    >
                      Likeness
                    </div>
                    <p style={{ fontSize: 11.5, color: "var(--ink-3)", margin: "0 0 12px" }}>
                      Audio-only measured closer to the reference than feeding the
                      clip's transcript as well, and kept better pacing — it won all
                      six model/temperature pairings, so it's the default.
                    </p>
                    <div className="voice-filter" style={{ marginBottom: 12 }}>
                      {(
                        [
                          ["reference", "Audio only"],
                          ["transcript", "Transcript + audio"],
                        ] as const
                      ).map(([key, label]) => (
                        <button
                          key={key}
                          type="button"
                          className={cloneMode === key ? "on" : ""}
                          onClick={() => setCloneMode(key)}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                    <ParamSlider
                      label="Temperature"
                      value={mossTemp}
                      onChange={setMossTemp}
                      min={0.4}
                      max={2.0}
                      step={0.05}
                    />
                    <p style={{ fontSize: 11, color: "var(--ink-4)", margin: "8px 0 0" }}>
                      MOSS ships at {MOSS_DEFAULT_TEMPERATURE}, which measured best
                      overall. Lower didn't reliably improve likeness and badly
                      overran the intended length — 1.7x at 0.9 on the 4B.
                    </p>
                  </div>
                )}

                {/* Chatterbox: fine-tune sliders */}
                {model === "chatterbox" && (
                  <div className="card" style={{ padding: 14, marginTop: 14 }}>
                    <button
                      type="button"
                      onClick={() => setSettingsOpen((v) => !v)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        width: "100%",
                        padding: 0,
                      }}
                    >
                      <span className="ph-eyebrow" style={{ margin: 0 }}>
                        Fine-tune
                      </span>
                      <span style={{ color: "var(--ink-3)", display: "grid", placeItems: "center" }}>
                        <Icon.Caret
                          style={{ transform: settingsOpen ? "rotate(180deg)" : "none", transition: "transform .2s" }}
                        />
                      </span>
                    </button>
                    {settingsOpen && (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 16,
                          marginTop: 14,
                          paddingTop: 14,
                          borderTop: "1px solid var(--hair)",
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "flex-end" }}>
                          <button
                            type="button"
                            className="btn btn-ghost btn-sm"
                            onClick={() => {
                              if (selectedVoice?.defaults) {
                                setParams({
                                  exaggeration:
                                    selectedVoice.defaults.exaggeration ?? DEFAULT_PARAMS.exaggeration,
                                  cfgWeight:
                                    selectedVoice.defaults.cfgWeight ?? DEFAULT_PARAMS.cfgWeight,
                                  temperature:
                                    selectedVoice.defaults.temperature ?? DEFAULT_PARAMS.temperature,
                                  repetitionPenalty:
                                    selectedVoice.defaults.repetitionPenalty ??
                                    DEFAULT_PARAMS.repetitionPenalty,
                                  topP: selectedVoice.defaults.topP ?? DEFAULT_PARAMS.topP,
                                  minP: selectedVoice.defaults.minP ?? DEFAULT_PARAMS.minP,
                                });
                              } else {
                                setParams(DEFAULT_PARAMS);
                              }
                            }}
                          >
                            Reset defaults
                          </button>
                        </div>

                        <ParamSlider
                          label="Exaggeration"
                          value={params.exaggeration}
                          onChange={(v) => setParams((p) => ({ ...p, exaggeration: v }))}
                          min={0}
                          max={1}
                          step={0.05}
                          presets={[
                            { label: "Subtle", value: 0.25 },
                            { label: "Normal", value: 0.5 },
                            { label: "Dramatic", value: 0.85 },
                          ]}
                        />
                        <ParamSlider
                          label="CFG Weight"
                          value={params.cfgWeight}
                          onChange={(v) => setParams((p) => ({ ...p, cfgWeight: v }))}
                          min={0}
                          max={1}
                          step={0.05}
                        />
                        <ParamSlider
                          label="Temperature"
                          value={params.temperature}
                          onChange={(v) => setParams((p) => ({ ...p, temperature: v }))}
                          min={0.1}
                          max={1.5}
                          step={0.05}
                          presets={[
                            { label: "Precise", value: 0.5 },
                            { label: "Balanced", value: 0.8 },
                            { label: "Creative", value: 1.2 },
                          ]}
                        />
                        <ParamSlider
                          label="Repetition Penalty"
                          value={params.repetitionPenalty}
                          onChange={(v) => setParams((p) => ({ ...p, repetitionPenalty: v }))}
                          min={1.0}
                          max={2.0}
                          step={0.05}
                        />
                        <ParamSlider
                          label="Top P"
                          value={params.topP}
                          onChange={(v) => setParams((p) => ({ ...p, topP: v }))}
                          min={0.5}
                          max={1.0}
                          step={0.05}
                        />
                        <ParamSlider
                          label="Min P"
                          value={params.minP}
                          onChange={(v) => setParams((p) => ({ ...p, minP: v }))}
                          min={0}
                          max={0.5}
                          step={0.01}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="wiz-foot">
          <div className="wiz-foot-l">
            <span className="ok">●</span>
            <span>
              {step === 0
                ? "selection autosaves"
                : `${text.length} chars · ~${Math.max(1, Math.round(text.length / 15))}s of audio`}
            </span>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button
              type="button"
              className={`btn btn-ghost${step === 0 ? " disabled" : ""}`}
              onClick={() => step > 0 && setStep((s) => (s - 1) as Step)}
            >
              <Icon.ArrowL /> Back
            </button>
            <button
              type="button"
              className={`btn btn-primary${
                step === 0 || text.length < 2 || submitting ? " disabled" : ""
              }`}
              onClick={() => {
                if (step === 0) return;
                void handleSubmit();
              }}
            >
              {submitting ? (
                <>
                  <span className="spin" /> Submitting…
                </>
              ) : step === 1 ? (
                <>
                  Generate takes <Icon.Arrow />
                </>
              ) : (
                <>
                  Continue <Icon.Arrow />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

interface ReferenceTrimProps {
  voice: Reference;
  trimStart: number;
  trimEnd: number;
  setTrimStart: (n: number) => void;
  setTrimEnd: (n: number) => void;
  editingDefault: boolean;
  setEditingDefault: (b: boolean) => void;
  open: boolean;
  setOpen: (b: boolean) => void;
  onSavedDefault: (v: Reference) => void;
}

function ReferenceTrim({
  voice,
  trimStart,
  trimEnd,
  setTrimStart,
  setTrimEnd,
  editingDefault,
  setEditingDefault,
  open,
  setOpen,
  onSavedDefault,
}: ReferenceTrimProps) {
  const hasClip = voice.defaultClipS3Key != null;
  const showOriginal = editingDefault || !hasClip;
  const audioForTrimmer = showOriginal
    ? voice.previewUrl ?? ""
    : voice.defaultClipPreviewUrl ?? voice.previewUrl ?? "";
  const durationForTrimmer = showOriginal
    ? voice.duration
    : voice.defaultClipDuration ?? voice.duration;

  const handleSaveDefault = async (start: number, end: number) => {
    const result = await updateReferenceDefaults(voice.name, start, end);
    onSavedDefault({
      ...voice,
      defaultTrimStart: start,
      defaultTrimEnd: end,
      defaultClipS3Key: result.defaultClipS3Key ?? undefined,
      defaultClipDuration: result.defaultClipDuration ?? undefined,
      defaultClipPreviewUrl: result.defaultClipPreviewUrl,
      transcript: result.transcript ?? undefined,
    });
    setEditingDefault(false);
  };

  const isLong = durationForTrimmer > LONG_AUDIO_THRESHOLD_S;

  return (
    <div className="card" style={{ padding: 14, marginTop: 14 }}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          padding: 0,
        }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span className="ph-eyebrow" style={{ margin: 0 }}>
            Reference trim
          </span>
          {hasClip && <span className="pill warn">⭐ default saved</span>}
        </span>
        <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 12, color: "var(--ink-3)", fontFamily: "JetBrains Mono, monospace" }}>
            {hasClip && !editingDefault
              ? `${durationForTrimmer.toFixed(1)}s clip`
              : `${(trimEnd - trimStart).toFixed(1)}s of ${voice.duration.toFixed(1)}s`}
          </span>
          <Icon.Caret
            style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform .2s", color: "var(--ink-3)" }}
          />
        </span>
      </button>

      {open && hasClip && !editingDefault && (
        <div
          style={{
            paddingTop: 14,
            borderTop: "1px solid var(--hair)",
            marginTop: 14,
            display: "flex",
            flexDirection: "column",
            gap: 12,
          }}
        >
          <div style={{ fontSize: 12.5, color: "var(--ink-3)", lineHeight: 1.5 }}>
            This voice uses a saved default clip ({durationForTrimmer.toFixed(1)}s). TTS jobs use
            the clip directly, so they don't re-process the full {voice.duration.toFixed(1)}s
            original.
          </div>
          <audio src={audioForTrimmer} controls preload="metadata" style={{ width: "100%" }} />
          {voice.transcript && (
            <div className="lib-quote" title={voice.transcript}>
              "{voice.transcript.length > 200
                ? voice.transcript.slice(0, 200) + "…"
                : voice.transcript}"
            </div>
          )}
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            style={{ alignSelf: "flex-start", color: "var(--amber)" }}
            onClick={() => {
              setTrimStart(voice.defaultTrimStart ?? 0);
              setTrimEnd(voice.defaultTrimEnd ?? Math.min(voice.duration, 60));
              setEditingDefault(true);
            }}
          >
            Change default
          </button>
        </div>
      )}

      {open && showOriginal && (
        <div
          style={{
            paddingTop: 14,
            borderTop: "1px solid var(--hair)",
            marginTop: 14,
            display: "flex",
            flexDirection: "column",
            gap: 10,
          }}
        >
          {hasClip && editingDefault && (
            <div style={{ fontSize: 12.5, color: "var(--ink-3)", lineHeight: 1.5 }}>
              Editing the default clip. Drag handles to choose a new region of the full{" "}
              {voice.duration.toFixed(1)}s original, then save.
            </div>
          )}
          {isLong ? (
            <>
              <WaveformTrimmer
                audioUrl={audioForTrimmer}
                duration={durationForTrimmer}
                onTrim={(start, end) => {
                  setTrimStart(start);
                  setTrimEnd(end);
                }}
              />
              <button
                type="button"
                className="btn btn-line btn-sm"
                style={{ alignSelf: "flex-start" }}
                onClick={() => handleSaveDefault(trimStart, trimEnd)}
              >
                <Icon.Star /> {hasClip ? "Update default" : "Set default"}
              </button>
            </>
          ) : (
            <AudioTrimmer
              audioUrl={audioForTrimmer}
              duration={durationForTrimmer}
              trimStart={trimStart}
              trimEnd={trimEnd}
              onChange={(start, end) => {
                setTrimStart(start);
                setTrimEnd(end);
              }}
              onSaveDefault={handleSaveDefault}
              hasSavedDefault={hasClip}
            />
          )}
          {hasClip && editingDefault && (
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              style={{ alignSelf: "flex-start" }}
              onClick={() => setEditingDefault(false)}
            >
              Cancel
            </button>
          )}
        </div>
      )}
    </div>
  );
}
